import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { locationQuestionData } from '@abvehicles/constants/questionData';
import { MapTemplate } from '@templates';
import { useAbVehiclesStore } from '@abvehicles/store/store';
import { AssetType, ErrorMessages } from '@types';
import { RoutesList } from '@abvehicles/routes/Routes';
import {
  addressResponseType,
  convertAddressToServiceLocation,
  convertServiceLocationToAddress,
  TextAreaMaxLengthSchema,
  parseZodErrors,
} from '@utils';
import { LatLngTuple } from 'leaflet';
import { ZodError } from 'zod';
import { CustomAddressContent } from './CustomAddressContent';

export const IssueLocationPage = () => {
  const navigate = useNavigate();
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const {
    serviceLocationDetails,
    updateServiceLocationDetails,
    isPhotoLocationStored,
  } = useAbVehiclesStore();
  const { locationStored, locationNotStored, additionalLocationDetails } =
    locationQuestionData;
  const { longitude, latitude } = serviceLocationDetails;
  // AssetType refers to https://www.rbkc.gov.uk/arcgis/rest/services/WCC/FixMyStreetOpen/MapServer
  const assetType: AssetType = 'HighwaysCarriageways';

  const navigationIfInsideTfLBoundary = () => {
    setErrorMessages([]);
    navigate(RoutesList.REPORT_TO_TFL);
    return;
  };

  const selectedAddress =
    latitude && longitude
      ? convertServiceLocationToAddress(serviceLocationDetails)
      : ({} as addressResponseType);
  const defaultLocation =
    selectedAddress?.latitude && selectedAddress?.longitude
      ? ([
          parseFloat(selectedAddress.latitude),
          parseFloat(selectedAddress.longitude),
        ] as LatLngTuple)
      : undefined;

  const customAddressComponent =
    isPhotoLocationStored &&
    selectedAddress?.longitude &&
    selectedAddress?.latitude ? (
      <CustomAddressContent
        selectedAddress={selectedAddress}
        locationTitle={
          isPhotoLocationStored ? 'Issue Location' : 'You have selected'
        }
      />
    ) : null;

  const buttonPrevious = {
    onClick: () => {
      navigate(RoutesList.VEHICLE_DETAILS);
    },
  };

  const buttonNext = {
    onClick: () => {
      setErrorMessages([]);
      try {
        const { latitude, longitude } = serviceLocationDetails;
        if (!latitude || !longitude) {
          setErrorMessages([
            {
              message: 'Please select a location on the map',
              path: locationQuestionData.errorPath,
            },
          ]);
          return;
        }
        TextAreaMaxLengthSchema({
          fieldNames: [additionalLocationDetails.id],
          maxLength: additionalLocationDetails.maxLength,
        }).parse({
          [additionalLocationDetails.id]:
            serviceLocationDetails.additional_details || '',
        });
        navigate(RoutesList.EMAIL_CONFIRMATION_REPORT);
      } catch (error) {
        const validationErrors = parseZodErrors(error as ZodError);
        setErrorMessages(
          validationErrors.map((error) => ({
            message: error.message,
            path: error.path,
          })),
        );
      }
    },
  };

  const handleChangeAddress = (address: addressResponseType) => {
    setErrorMessages([]);
    const formattedAddress = convertAddressToServiceLocation(address);
    updateServiceLocationDetails({
      ...serviceLocationDetails,
      ...formattedAddress,
    });
  };

  const additionalLocationDetailsWithOnChange = {
    ...additionalLocationDetails,
    isLabelTextTitle: false,
    name: additionalLocationDetails.id,
    onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      updateServiceLocationDetails({
        ...serviceLocationDetails,
        additional_details: e.target.value,
      });
    },
    value: serviceLocationDetails.additional_details || '',
    showCounter: true,
    maxLength: 500,
    errorMessages: errorMessages.filter(
      (error) => error.path === additionalLocationDetails.id,
    ),
  };

  return (
    <MapTemplate
      id={locationQuestionData.id}
      labelText={
        isPhotoLocationStored
          ? locationStored.labelText
          : locationNotStored.title
      }
      subText={
        isPhotoLocationStored
          ? locationStored.subText
          : locationNotStored.subText
      }
      errorMessages={errorMessages}
      errorPath={locationQuestionData.errorPath}
      selectedAddress={selectedAddress}
      buttonNext={buttonNext}
      buttonPrevious={buttonPrevious}
      onChangeAddress={handleChangeAddress}
      defaultLocation={defaultLocation}
      customAddressContent={customAddressComponent}
      additionalLocationDetailsTextArea={additionalLocationDetailsWithOnChange}
      enableTfLBoundaryCheck={true}
      navigationIfInsideTfLBoundary={navigationIfInsideTfLBoundary}
      assetType={assetType}
    />
  );
};
